import { Component, inject, OnInit, SecurityContext } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  CitationType,
  ContentType,
  ExpectedCitation,
  TestResult,
  TestResultCitation,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { formatVoiceConfig, isVoiceTest, StatusMap } from '../test-run.utils';
import { CommonModule } from '@angular/common';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import {
  TestToolCallModalComponent,
  TestToolCallModalData,
} from '../test-tool-call-modal/test-tool-call-modal.component';
import { MarkdownService, SECURITY_CONTEXT } from 'ngx-markdown';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { TestChatHistoryComponent } from '../test-chat-history/test-chat-history.component';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';

@Component({
  selector: 'ai-test-results-modal',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    GalaxyUploaderModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatInputModule,
    MatDividerModule,
    MatListModule,
    TestChatHistoryComponent,
    AiAssistantI18nModule,
    GalaxyBadgeModule,
  ],
  templateUrl: './test-results-modal.component.html',
  styleUrl: './test-results-modal.component.scss',
  providers: [MarkdownService, { provide: SECURITY_CONTEXT, useValue: SecurityContext.HTML }],
})
export class TestResultsModalComponent implements OnInit {
  protected data = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<TestResultsModalComponent, void>);
  testResult: TestResult;
  expectedCitations: ExpectedCitation[] = [];
  isVoice = false;
  voiceConfigLabel = '';
  audioError = false;
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);

  ngOnInit() {
    this.data.citations =
      this.data.citations?.filter((c: TestResultCitation) => {
        return c.type != 'functionCall';
      }) || [];
    this.testResult = this.data;
    this.expectedCitations = (this.testResult.expectedCitations || []).filter(
      (c) => c.title || c.link || (c.type && c.type !== 'UNSPECIFIED'),
    );
    this.isVoice = isVoiceTest(this.testResult?.testType);
    this.voiceConfigLabel = formatVoiceConfig(this.testResult?.voiceConfig);
  }

  getContentTypeName(contentType: ContentType): string {
    switch (contentType) {
      case ContentType.REASONING:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.REASONING');
      case ContentType.ANSWER:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.ANSWER');
      case ContentType.ACTION_DESCRIPTION:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.ACTION_DESCRIPTION');
      case ContentType.WORKING:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.WORKING');
      default:
        return this.translateService.instant('AI_ASSISTANT.TESTING.CONTENT_TYPE.UNSPECIFIED');
    }
  }

  getCitationTypeName(type: CitationType): string {
    switch (type) {
      case 'WEBSITE':
        return this.translateService.instant('AI_ASSISTANT.TESTING.EDIT_MODAL.CITATION_TYPE_WEBSITE');
      case 'BUSINESS_PROFILE':
        return this.translateService.instant('AI_ASSISTANT.TESTING.EDIT_MODAL.CITATION_TYPE_BUSINESS_PROFILE');
      case 'FILE':
        return this.translateService.instant('AI_ASSISTANT.TESTING.EDIT_MODAL.CITATION_TYPE_FILE');
      case 'USER_INPUT':
        return this.translateService.instant('AI_ASSISTANT.TESTING.EDIT_MODAL.CITATION_TYPE_USER_INPUT');
      default:
        return this.translateService.instant('AI_ASSISTANT.TESTING.EDIT_MODAL.CITATION_TYPE_ANY');
    }
  }

  openToolCallCitationDetails(name: string, args: string, response: string): void {
    this.dialog.open(TestToolCallModalComponent, {
      width: '950px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: {
        title: name,
        arguments: args,
        response,
      } as TestToolCallModalData,
      autoFocus: false,
    });
  }

  protected readonly statusMap = StatusMap;
}
